import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch, type Page } from "./client";
import { accountsQueryKey } from "./accounts";

/** `model.md` §2.1's money block on the wire. Minor units, never a formatted string. */
export interface Money {
  amount_minor: number;
  currency_code: string;
  base_amount_minor: number;
  base_fx_rate: string;
  original_amount_minor?: number;
  original_currency_code?: string;
  original_fx_rate?: string;
}

/** One line of the receipt. */
export interface Split {
  id: string;
  category_id?: string;
  money: Money;
  note?: string;
  sort_order: number;
  is_business: boolean;
  vat_rate_bp?: number;
  vat_amount_minor?: number;
  net_amount_minor?: number;
}

/**
 * A transaction always arrives with its splits, because `model.md` §5.2 makes
 * them one object: a header without them is a total with nothing saying what it
 * was for.
 */
export interface Transaction {
  id: string;
  account_id: string;
  booked_date: string;
  value_date?: string;
  money: Money;
  payee_id?: string;
  payee_raw?: string;
  /** Non-null on both legs of a transfer (`model.md` §5.7). */
  transfer_group_id?: string;
  note?: string;
  reference_text?: string;
  is_cleared: boolean;
  bank_reference?: string;
  source: string;
  splits: Split[];
  created_at: string;
  updated_at: string;
}

export interface TransactionFilter {
  accountId?: string;
}

export const transactionsQueryKey = (householdId: string, filter: TransactionFilter = {}) =>
  ["households", householdId, "transactions", filter.accountId ?? "all"] as const;

/**
 * The ledger, newest booked date first, walked by cursor.
 *
 * Cursor rather than offset (`architecture-guide.md` §4.11): a ledger grows at
 * the end a reader starts from, so page two of an offset query silently repeats
 * or skips rows as soon as anything is recorded between requests.
 */
export function useTransactions(householdId: string | undefined, filter: TransactionFilter = {}) {
  return useInfiniteQuery({
    queryKey: transactionsQueryKey(householdId ?? "", filter),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) => {
      const params = new URLSearchParams({ limit: "50" });
      if (filter.accountId) params.set("account_id", filter.accountId);
      if (pageParam) params.set("cursor", pageParam);
      return apiFetch<Page<Transaction>>(
        `/api/v1/households/${householdId}/transactions?${params.toString()}`,
        { signal },
      );
    },
    getNextPageParam: (last) => last.pagination.next_cursor ?? undefined,
    enabled: Boolean(householdId),
  });
}

/**
 * The body of `POST /transactions`.
 *
 * No `splits`: this is K-10's single-split fast path, which is the whole of the
 * thin path. One amount, one date, and the server makes the split — the entry
 * form never shows the concept (`model.md` §3.5).
 */
export interface CreateTransactionInput {
  account_id: string;
  booked_date: string;
  /** Signed minor units. Expenses negative (`model.md` §2.2 rule 1). */
  amount_minor: number;
  /**
   * Optional. Omitting it records an uncategorised transaction, which is a real
   * state in the model rather than a missing one (`model.md` §3.4).
   */
  category_id?: string;
  payee_id?: string;
  payee_raw?: string;
  /** Non-null on both legs of a transfer (`model.md` §5.7). */
  transfer_group_id?: string;
  note?: string;
}

/**
 * Records a transaction.
 *
 * Both the ledger and the account list are invalidated, because a transaction
 * changes a balance and the balance is derived — there is no stored number to
 * patch, and guessing the new one in the cache would be inventing a second
 * source of truth for exactly the figure `model.md` §5.1 says must have one.
 */
export function useCreateTransaction(householdId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTransactionInput) =>
      apiFetch<Transaction>(`/api/v1/households/${householdId}/transactions`, {
        method: "POST",
        body: input,
      }),
    // The promise is returned, not discarded: react-query waits for it before
    // the mutation settles, so a caller that navigates on success arrives at a
    // list that already has the new row. Discarding it races the redirect
    // against the refetch, and the ledger shows the transaction only sometimes.
    // The payees go with them: a booking can create a payee that did not exist a
    // moment ago, and it always moves the counters the autocomplete is ordered
    // by. Without this, a name typed once while it was still unknown stays
    // unknown for as long as the cached answer lives — which is exactly the
    // second time somebody types it, the moment K-14 exists to serve.
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ["households", householdId, "transactions"] }),
        client.invalidateQueries({ queryKey: accountsQueryKey(householdId) }),
        client.invalidateQueries({ queryKey: ["households", householdId, "payees"] }),
      ]),
  });
}
